import { Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { type AudiSalesForceModuleSpecsFragment } from '../../../../../api/fragments';
import {
    CreateAudiSalesForceModuleDocument,
    type CreateAudiSalesForceModuleMutation,
    type CreateAudiSalesForceModuleMutationVariables,
} from '../../../../../api/mutations/createAudiSalesForceModule';
import { type AudiSalesForceModuleSettings } from '../../../../../api/types';
import InputField from '../../../../../components/fields/InputField';
import PasswordField from '../../../../../components/fields/PasswordField';
import SelectField from '../../../../../components/fields/SelectField';
import SingleSecretUploadField from '../../../../../components/fields/SingleSecretUploadField';
import validators from '../../../../../utilities/validators';
import { type CompanySpecs } from '../../../AddModulePage/getAvailableModules';
import AudiSalesForceModulePage, { type AudiSalesForceModulePageProps } from '../../AudiSalesForceModulePage';
import useModuleOptions from './shared/useModuleOptions';
import { type ModuleImplementation } from './types';

type AudiSalesForceModuleImplementation = ModuleImplementation<
    AudiSalesForceModuleSpecsFragment,
    AudiSalesForceModuleSettings
>;

const formValidator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('setting.baseUrl'),
    validators.requiredString('setting.clientId'),
    validators.requiredString('setting.clientSecret')
);

const colSpan = { md: 12, xs: 24 };

type FormProps = {
    company: CompanySpecs;
};

const Form: AudiSalesForceModuleImplementation['FormComponent'] = ({ company }: FormProps) => {
    const { t } = useTranslation(['moduleDetails']);

    const { tradeIn: tradeInOptions } = useModuleOptions(company);

    return (
        <Row gutter={10}>
            <Col {...colSpan}>
                <InputField
                    {...t('moduleDetails:fields.displayName', { returnObjects: true })}
                    name="displayName"
                    required
                />
            </Col>

            <Col {...colSpan}>
                <SelectField
                    {...t('moduleDetails:fields.tradeInModuleId', { returnObjects: true })}
                    name="setting.tradeInModuleId"
                    options={tradeInOptions}
                    required
                    showSearch
                />
            </Col>

            <Col {...colSpan}>
                <InputField
                    {...t('moduleDetails:fields.baseUrl', { returnObjects: true })}
                    name="setting.baseUrl"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('moduleDetails:fields.clientId', { returnObjects: true })}
                    name="setting.clientId"
                    required
                />
            </Col>

            <Col {...colSpan}>
                <PasswordField
                    {...t('moduleDetails:fields.clientSecret', { returnObjects: true })}
                    name="setting.clientSecret"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('moduleDetails:fields.authority', { returnObjects: true })}
                    name="setting.authority"
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('moduleDetails:fields.publicCert', { returnObjects: true })}
                    name="setting.publicCert"
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('moduleDetails:fields.privateKey', { returnObjects: true })}
                    name="setting.privateKey"
                />
            </Col>
        </Row>
    );
};

const implementation: AudiSalesForceModuleImplementation = {
    PageComponent: ({ module }: AudiSalesForceModulePageProps) => <AudiSalesForceModulePage module={module} />,
    getTitle: t => t('moduleDetails:implementations.AudiSalesForceModule.className'),
    FormComponent: Form,
    getValidator: () => formValidator,
    create: async (apolloClient, companyId, settings) =>
        apolloClient
            .mutate<CreateAudiSalesForceModuleMutation, CreateAudiSalesForceModuleMutationVariables>({
                mutation: CreateAudiSalesForceModuleDocument,
                variables: { companyId, settings },
            })
            .then(({ data }) => data.module),
};

export default implementation;
